import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
} from "@mui/material";

// Split pattern keeps the matched address as its own array entry; the exact
// pattern re-tests those entries. Two regexes rather than one because a /g
// regex carries lastIndex between .test() calls and would alternate results.
const EMAIL_SPLIT = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
const EMAIL_EXACT = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/**
 * Renders a definition, turning any email address into a mailto link.
 *
 * Definitions stay plain strings in liftFieldDefinitions.js so the approved
 * copy is reviewable and assertable in tests; the linking is presentation and
 * belongs here.
 */
function renderDescription(desc) {
  if (!desc) {
    return desc;
  }
  return String(desc)
    .split(EMAIL_SPLIT)
    .map((part, index) =>
      EMAIL_EXACT.test(part) ? (
        <Link
          key={`${part}-${index}`}
          href={`mailto:${part}`}
          sx={{ color: "#2027A5", textDecoration: "underline" }}
        >
          {part}
        </Link>
      ) : (
        part
      )
    );
}

/**
 * Name/description reference table in a dialog, used for the
 * "Additional Information" field definitions.
 *
 * Extracted so the Lifts page can reuse the Route Status presentation instead
 * of carrying a third copy of the same markup (iris-ui RouteDataTable,
 * diot-insights-ui and sku360-ui each have their own). RouteDataTable can be
 * migrated onto this component separately; it is left alone here to keep this
 * change scoped to the Lifts page.
 *
 * Two defects in the original markup are deliberately not reproduced:
 *   - DialogContentText renders a <p> by default, so nesting a <table> inside
 *     it produces invalid HTML and a React DOM nesting warning. Rendered as a
 *     div here instead.
 *   - The original puts onClick on CloseIcon rather than the enclosing
 *     IconButton, so the button is unreachable by keyboard. The handler and an
 *     aria-label are on the button here.
 *
 * @param {boolean}  open      Whether the dialog is visible.
 * @param {Function} onClose   Called on backdrop click, Escape, or close button.
 * @param {Array}    rows      [{name, desc}] definitions to render, in order.
 * @param {string}   title     Dialog heading.
 * @param {string}   maxWidth  CSS width cap for the dialog paper.
 */
function AdditionalInfoDialog({
  open,
  onClose,
  rows = [],
  title = "Additional Information",
  maxWidth = "1000px",
}) {
  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth,
          },
        },
      }}
      aria-labelledby="additional-info-dialog-title"
      aria-describedby="additional-info-dialog-description"
    >
      <DialogTitle
        id="additional-info-dialog-title"
        sx={{ fontSize: "20px", padding: "10px", paddingBottom: "0px" }}
      >
        {title}
        <IconButton
          style={{ float: "right" }}
          onClick={onClose}
          aria-label="Close additional information"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="additional-info-dialog-description" component="div">
          <Box sx={{ width: "100%" }}>
            <TableContainer>
              <Table
                sx={{ minWidth: 850, fontSize: "25px", border: "1px solid #000" }}
                aria-label={title}
              >
                <TableHead>
                  <TableRow className={"btn-primary"}>
                    <TableCell align="center" className={"tb-header-font"}>
                      Name
                    </TableCell>
                    <TableCell align="center" className={"tb-header-font"}>
                      Description
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.name}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      {/* Left-aligned, not justified: justification stretches
                          word spacing to fill the column, which turns a
                          wrapping name like "Last Valid Location" into
                          "Last      Valid Location". */}
                      <TableCell align="left" component="th" scope="row" width={150}>
                        {row.name}
                      </TableCell>
                      <TableCell align="justify">{renderDescription(row.desc)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
}

export default AdditionalInfoDialog;
